import { useQuery, UseQueryOptions } from '@tanstack/react-query'

import { TANSTACK_QUERY_KEY } from '@/constants/tanstack-query'
import {
  IEstimateTrade,
  IGetTradeHasCommitmentQuotesResponse,
  IHttpError,
  ListTradeParams,
  Trade,
  TradeDetailOptions,
} from '@/data'
import { EstimateTradeRequest, ITrade, ITradeRes, TradeStatus } from '@/types'
import { getBtcConfirmationCount, isBtcChain } from '@/utils'

export function useListTrades(
  param: ListTradeParams,
  opt?: Omit<UseQueryOptions<ITradeRes, IHttpError>, 'queryKey' | 'queryFn'>
) {
  return useQuery<ITradeRes, IHttpError>({
    queryKey: [TANSTACK_QUERY_KEY.TRADE.GET_LIST_TRADE, param],
    queryFn: async () => {
      const response = await Trade.getListTrade(param)
      return response
    },
    gcTime: 30_000,
    staleTime: 0,
    enabled: param?.fromAddress?.length > 0,
    ...opt,
  })
}

export function useTradeDetail(tradeId: string, options?: TradeDetailOptions) {
  return useQuery<ITrade, IHttpError>({
    queryKey: [TANSTACK_QUERY_KEY.TRADE.GET_TRADE_DETAIL, tradeId],
    queryFn: async () => {
      const response = await Trade.getDetail(tradeId)

      return response.data
    },
    enabled: !!tradeId && !options?.skipInitialFetch,
    refetchInterval: (query) => {
      if (!options || !tradeId.startsWith('0x')) return false
      if (!query.state.data) return false
      const isStatus = [TradeStatus.REFUNDED, TradeStatus.SETTLEMENT_CONFIRMED].includes(
        query.state.data?.status as TradeStatus
      )
      return options.isRefetchInterval || !isStatus ? 7500 : false
    },
    retry: (failureCount) => {
      return failureCount <= 2
    },
    retryDelay: 1000,
  })
}

export function useGetBtcConfirmationCount({
  txId,
  network,
  enabled,
}: {
  txId: string
  network: string
  enabled: boolean
}) {
  return useQuery<number, IHttpError>({
    queryKey: [TANSTACK_QUERY_KEY.TRADE.GET_BTC_CONFIRMATION_COUNT, txId, network],
    queryFn: async () => {
      return getBtcConfirmationCount(txId, network)
    },
    enabled: Boolean(enabled && txId) && isBtcChain(network),
    refetchInterval: 15_000,
  })
}

const ESTIMATE_TRADE_KEY = 'ESTIMATE_TRADE_KEY'

export function useEstimateTrade(param: EstimateTradeRequest) {
  return useQuery<IEstimateTrade, IHttpError>({
    queryKey: [ESTIMATE_TRADE_KEY, param.fromToken, param.toToken, param.type],
    queryFn: async () => {
      const response = await Trade.estimate(param)

      return response.data
    },
    enabled: !!param.fromToken && !!param.toToken,
    gcTime: 15_000,
    staleTime: 15_000,
  })
}

export function useGetTradeHasCommitmentQuotes(
  tradeId: string,
  opt?: Omit<UseQueryOptions<IGetTradeHasCommitmentQuotesResponse, IHttpError>, 'queryKey' | 'queryFn'>
) {
  return useQuery<IGetTradeHasCommitmentQuotesResponse, IHttpError>({
    queryKey: [TANSTACK_QUERY_KEY.TRADE.GET_TRADE_COMMITMENT_QUOTES, tradeId],
    queryFn: async () => {
      const response = await Trade.getTradeHasCommitmentQuotes(tradeId)
      return response
    },
    ...opt,
  })
}
